import { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import Button from "@mui/material/Button";
import AccountBalanceOutlined from "@mui/icons-material/AccountBalanceOutlined";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import SetFundingEntityModal from "../../../../components/common/Loan/SetFundingEntityModal";
import { useDrilldownBaseUrl } from "../../../../pipelines/PipelineContext";
import { T, labelSx } from "./wireFundingShared";

/**
 * What the Wire & Funding tab shows when the loan has no funding entity.
 *
 * The funding entity is the party that actually sends the wire — the named
 * account holder on the Net Wire Calculator (`fundingEntityName`), the lender
 * block on the Funding Shield sheet (`fundingEntity.mortgageeClause`), and the
 * mortgagee on the note. Every artifact on this tab identifies the funder
 * somewhere, so working the tab without one produces documents that are blank in
 * exactly the place a counterparty checks.
 *
 * Two ways out, both landing on the same write:
 *   • set it here, in `SetFundingEntityModal` — the SAME dialog the Loan Terms
 *     tab opens from "Set Funding Entity", not a second copy of it;
 *   • go to Loan Terms, where the rest of the assignment lives (title contact,
 *     mortgagee clause) if more than the entity is missing.
 *
 * Plus `onSkip` — a quiet third control that dismisses the gate for the current
 * loan so the tab can be read without the entity. It is a view-only escape
 * hatch, not a fix: anything generated while skipped is still missing its
 * funder, and the gate returns on the next visit.
 */
const FundingEntityGate = ({ dealId, onSkip }) => {
  const navigate = useNavigate();
  const baseUrl = useDrilldownBaseUrl(dealId);
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Flx
        column
        center
        gap={2}
        sx={{
          bgcolor: T.card,
          border: `1px solid ${T.line}`,
          borderRadius: T.radius,
          boxShadow: T.shadow,
          px: 3,
          py: 7,
          textAlign: "center",
          // Empty state — a compact centered card rather than a full-width band.
          maxWidth: 560,
          mx: "auto",
        }}
      >
        <Flx
          center
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: T.amberTint,
            color: T.amber,
          }}
        >
          <AccountBalanceOutlined sx={{ fontSize: 24 }} />
        </Flx>

        <Flx column gap={0.75} sx={{ alignItems: "center", maxWidth: 460 }}>
          <Txt sx={{ ...labelSx, color: T.amber }}>Funding entity required</Txt>
          <Txt sx={{ fontSize: 16, fontWeight: 700, color: T.ink }}>
            This loan has no funding entity assigned
          </Txt>
          <Txt sx={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
            It names the party that sends the wire. Assign it to work this tab.
          </Txt>
        </Flx>

        <Flx column center gap={1} sx={{ mt: 1 }}>
          <Flx wrap center gap={1.5}>
            {/* No `className="thin"` on this icon: the theme paints a
                primary-colored stroke on `.MuiButton-contained svg.thin`, which
                outlined the glyph in blue against the teal fill. */}
            <Button
              size="large"
              variant="contained"
              startIcon={<EditOutlined />}
              onClick={() => setShowModal(true)}
            >
              Set funding entity
            </Button>
            <Button
              size="large"
              variant="outlined"
              endIcon={<ArrowForwardRounded />}
              onClick={() => navigate(`${baseUrl}/terms`)}
            >
              Go to Loan Terms
            </Button>
          </Flx>

          {/* Deliberately the quietest control on the card. Skipping does not
              make the tab safe to work — the documents still print without a
              funder — so it reads as an escape hatch for looking around, not a
              third equal way forward. Text variant, no color override: the
              theme owns the button color. */}
          {onSkip && (
            <Button size="small" variant="text" onClick={onSkip}>
              Skip for now
            </Button>
          )}
        </Flx>
      </Flx>

      <SetFundingEntityModal
        show={showModal}
        onClose={() => setShowModal(false)}
        dealId={dealId}
      />
    </>
  );
};

FundingEntityGate.propTypes = {
  dealId: PropTypes.string,
  /** Dismiss the gate for this loan and render the tab anyway. */
  onSkip: PropTypes.func,
};

export default FundingEntityGate;
